'use client';

import { Calendar, Timer, User } from 'lucide-react';
import React, { useEffect } from 'react';
import { BiCategory } from 'react-icons/bi';
import { GrLanguage } from 'react-icons/gr';
import { TbTimeDuration30 } from 'react-icons/tb';

import { useAxios } from '@/hooks';
import { cn } from '@/lib';
import { endPoint, getEventDateLabel } from '@/utils';
import { useSearchParams } from 'next/navigation';
import { GetTicketsBtn, SpecItem } from './components';
import { useDispatch } from 'react-redux';
import { AppDispatch } from '@/redux/store';
import { setCartData } from '@/redux/actions/user.action';

type EventSpecsProps = {
  data: any;
  eventId: string;
  eventCode: string;
  className?: string;
};

const EventSpecs: React.FC<EventSpecsProps> = ({
  data,
  eventId,
  eventCode,
  className,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const searchParams = useSearchParams();
  const influencerCode = searchParams.get('code');
  const { fetchData } = useAxios();

  const logInfluencerVisit = async () => {
    if (eventId && influencerCode) {
      await fetchData({
        url: endPoint.UPDATE_INFLUENCER_VISIT,
        method: 'POST',
        params: {
          eventId,
          influencerCode,
        },
      });
    }
  };

  useEffect(() => {
    logInfluencerVisit();
    dispatch(setCartData({}, {}, {}));
  }, []);

  const specs = [
    {
      condition: data?.duration,
      icon: <TbTimeDuration30 className='size-5 text-primary' />,
      label: data?.duration,
      title: 'Duration',
    },
    {
      condition: data?.eventTime,
      icon: <Timer className='size-5 text-primary' />,
      label: data?.eventTime,
      title: 'Time',
    },
    {
      condition: data?.eventDate || data?.dateString,
      icon: <Calendar className='size-5 text-primary' />,
      label: getEventDateLabel(data),
      title: 'Date',
    },
    {
      condition: data?.contentType,
      icon: <User className='size-5 text-primary' />,
      label: data?.contentType,
      title: 'Content Type',
    },
    {
      condition: data?.language,
      icon: <GrLanguage className='size-5 text-primary' />,
      label: data?.language,
      title: 'Language',
    },
    {
      condition: data?.categoryDisplay,
      icon: <BiCategory className='size-5 text-primary' />,
      label: data?.categoryDisplay,
      title: 'Category',
    },
  ];

  const isBookingClosed =
    (data?.onlineBookingEndTime
      ? new Date(data?.onlineBookingEndTime) < new Date()
      : new Date(data?.endDate) < new Date()) ||
    (data?.endDate && new Date(data?.endDate) < new Date()) ||
    data?.ticketTypes?.length === 0;

  const getEventStatus = () => {
    const now = Date.now();
    const startTime = data?.onlineBookingStartTime || data.startDate;
    const endTime = data?.onlineBookingEndTime || data.endDate;

    if (now < startTime) return 'Upcoming';
    if (now > endTime) return 'Ended';
    return startTime && endTime ? 'Ongoing' : 'Unknown';
  };

  return (
    <div
      className={cn(
        'overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm',
        className,
      )}
    >
      {/* The event name lives in the page <h1> above — kept out of the card
          to avoid showing the title twice. */}
      <div className='grid grid-cols-1 gap-x-3 gap-y-5 p-5 sm:grid-cols-2'>
        {specs
          .filter((spec) => spec.condition)
          .map((spec, index) => (
            <SpecItem
              key={index}
              icon={spec.icon}
              title={spec.title}
              label={spec.label}
            />
          ))}
      </div>

      <GetTicketsBtn
        eventStatus={getEventStatus()}
        eventCode={eventCode}
        isBookingClosed={data?.isRecurringEvent ? false : isBookingClosed}
        ticketPriceDisplay={data?.ticketPriceDisplay}
        onlineBookingStartText={data?.onlineBookingStartText}
        onlineBookingEndText={data?.onlineBookingEndText}
      />
    </div>
  );
};

export default EventSpecs;
